'use client'

import { Image, PageIndicatorDot } from '@macarongfactory/web-ui'
import { useEffect, useRef, useState } from 'react'

import { type ISeoPlace } from '@/features/seoPlaceDetail/model'

interface IPlacePhotoProps {
  place: ISeoPlace
}

const PlacePhoto = ({ place }: IPlacePhotoProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({ isDragging: false, startX: 0, startScrollLeft: 0 })

  const photos = Array.from(
    new Map(
      (place.images ?? []).map(image => [
        `${image.macarongObjectId}-${image.displayOrder}`,
        image,
      ])
    ).values()
  )

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragRef.current.isDragging || !scrollerRef.current) return
      event.preventDefault()
      const dx = event.clientX - dragRef.current.startX
      scrollerRef.current.scrollLeft = dragRef.current.startScrollLeft - dx
    }
    const handleMouseUp = () => {
      dragRef.current.isDragging = false
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  if (photos.length === 0) return null

  const handleScroll = () => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const index = Math.round(scroller.scrollLeft / scroller.clientWidth)
    if (index !== activeIndex) setActiveIndex(index)
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!scrollerRef.current) return
    dragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: scrollerRef.current.scrollLeft,
    }
  }

  return (
    <section
      aria-label='정비소 사진'
      className='relative h-[180px] w-full select-none overflow-hidden lg:aspect-[2/1] lg:h-auto'
    >
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        className='scrollbar-hide flex h-full w-full cursor-grab snap-x snap-mandatory overflow-x-auto active:cursor-grabbing'
      >
        {photos.map((image, index) => (
          <div
            key={`${image.macarongObjectId}-${image.displayOrder}`}
            className='relative h-full w-full shrink-0 snap-start'
          >
            <Image
              src={image.url}
              alt={`${place.placeName} 이미지 ${index + 1}`}
              width='100%'
              height='100%'
              radius={0}
              scaletype='crop'
              className='absolute inset-0'
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : undefined}
              draggable={false}
            />
          </div>
        ))}
      </div>
      {photos.length > 1 && (
        <div className='pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2'>
          <PageIndicatorDot
            total={photos.length}
            activeIndex={activeIndex}
            variant='white'
          />
        </div>
      )}
    </section>
  )
}

export default PlacePhoto
